import { defineStore } from 'pinia';
import axios from 'axios';

export const useSettingsStore = defineStore('settings', {
    state: () => ({
        settings: (typeof window !== 'undefined' ? window.initialSettings : null) || {},
        loading: false,
        error: null,
    }),

    getters: {
        // Get a specific setting value
        getSetting: (state) => (key, defaultValue = null) => {
            return state.settings[key] || defaultValue;
        },

        // Site information
        siteName: (state) => state.settings.site_name || 'Qorvin IT',
        siteTagline: (state) => state.settings.site_tagline || 'We build the future',
        footerText: (state) => state.settings.footer_text || '© 2025 Qorvin IT. All rights reserved.',

        // Social links
        facebook: (state) => state.settings.facebook || '',
        linkedin: (state) => state.settings.linkedin || '',
        instagram: (state) => state.settings.instagram || '',
        youtube: (state) => state.settings.youtube || '',

        // Theme
        themeMode: (state) => state.settings.theme_mode || 'light',

        // Logos and images
        logo: (state) => {
            const path = state.settings.logo_light || state.settings.logo_dark;
            return path ? `/storage/${path}` : null;
        },
        logoLight: (state) => state.settings.logo_light ? `/storage/${state.settings.logo_light}` : null,
        logoDark: (state) => state.settings.logo_dark ? `/storage/${state.settings.logo_dark}` : null,
        favicon: (state) => state.settings.favicon ? `/storage/${state.settings.favicon}` : null,
        ogImage: (state) => state.settings.og_image ? `/storage/${state.settings.og_image}` : null,

        // SEO
        metaTitle: (state) => state.settings.meta_title || 'Qorvin IT — Leading Software Company',
        metaDescription: (state) => state.settings.meta_description || '',
        metaKeywords: (state) => state.settings.meta_keywords || '',

        // Contact
        contactEmail: (state) => state.settings.contact_email || 'info@Qorvin IT.com',
        contactPhone: (state) => state.settings.contact_phone || '+1 (555) 123-4567',
        contactAddress: (state) => state.settings.contact_address || '',

        // Live Chat
        liveChatEnabled: (state) => state.settings.live_chat_enabled || 'false',

        // Hero section
        heroTitle: (state) => state.settings.hero_title || 'Qorvin IT Solutions',
        heroSubtitle: (state) => state.settings.hero_subtitle || 'We deliver cutting-edge technology solutions',
        heroCtaText: (state) => state.settings.hero_cta_text || 'Get Started',
        heroCtaLink: (state) => state.settings.hero_cta_link || '/contact',
        heroSecondaryCtaText: (state) => state.settings.hero_secondary_cta_text || 'Learn More',
        heroSecondaryCtaLink: (state) => state.settings.hero_secondary_cta_link || '/about',
    },

    actions: {
        /**
         * Fetch all settings from API
         */
        async fetchSettings() {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get('/api/settings');
                if (response.data.success) {
                    this.settings = response.data.settings;
                }
            } catch (error) {
                this.error = error.response?.data?.message || 'Failed to fetch settings';
                console.error('Error fetching settings:', error);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Update settings
         */
        async updateSettings(settingsArray) {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.post('/api/admin/settings/update', {
                    settings: settingsArray,
                });

                if (response.data.success) {
                    this.settings = response.data.settings;
                    return { success: true, message: response.data.message };
                }
            } catch (error) {
                this.error = error.response?.data?.message || 'Failed to update settings';
                return { success: false, message: this.error };
            } finally {
                this.loading = false;
            }
        },

        /**
         * Upload logo
         */
        async uploadLogo(file, type = 'light') {
            this.loading = true;
            this.error = null;

            try {
                const formData = new FormData();
                formData.append('logo', file);
                formData.append('type', type);

                const response = await axios.post('/api/admin/settings/upload-logo', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.data.success) {
                    // Update both logo keys in local state
                    this.settings.logo_light = response.data.path;
                    this.settings.logo_dark = response.data.path;
                    return { success: true, message: response.data.message, url: response.data.url };
                }
            } catch (error) {
                this.error = error.response?.data?.message || 'Failed to upload logo';
                return { success: false, message: this.error };
            } finally {
                this.loading = false;
            }
        },

        /**
         * Upload favicon
         */
        async uploadFavicon(file) {
            this.loading = true;
            this.error = null;

            try {
                const formData = new FormData();
                formData.append('favicon', file);

                const response = await axios.post('/api/admin/settings/upload-favicon', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.data.success) {
                    this.settings.favicon = response.data.path;
                    return { success: true, message: response.data.message, url: response.data.url };
                }
            } catch (error) {
                this.error = error.response?.data?.message || 'Failed to upload favicon';
                return { success: false, message: this.error };
            } finally {
                this.loading = false;
            }
        },

        /**
         * Upload OG image
         */
        async uploadOgImage(file) {
            this.loading = true;
            this.error = null;

            try {
                const formData = new FormData();
                formData.append('og_image', file);

                const response = await axios.post('/api/admin/settings/upload-og', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.data.success) {
                    this.settings.og_image = response.data.path;
                    return { success: true, message: response.data.message, url: response.data.url };
                }
            } catch (error) {
                this.error = error.response?.data?.message || 'Failed to upload OG image';
                return { success: false, message: this.error };
            } finally {
                this.loading = false;
            }
        },

        /**
         * Set a single setting
         */
        setSetting(key, value) {
            this.settings[key] = value;
        },
    },
});
